<script lang="ts" setup>
/**
 * 多行自适应文本:text 中的 \n 是「建议断点」,而非强制换行。
 * 整句一行放得下时按单行完整展示(忽略断点);
 * 放不下时按断点拆行,每行用 BasicFitText 等比缩放,保证不折行、不省略。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import BasicFitText from './BasicFitText.vue'

const props = defineProps<{
  text?: string
}>()

const lines = computed(() => String(props.text || '').split('\n').filter(Boolean))
const singleLine = computed(() => lines.value.join(' '))

const containerRef = ref<HTMLElement>()
const measureRef = ref<HTMLElement>()
const fitsSingleLine = ref(true)

let observer: ResizeObserver | null = null

function update() {
  const available = containerRef.value?.clientWidth ?? 0
  const natural = measureRef.value?.scrollWidth ?? 0
  fitsSingleLine.value = natural <= available
}

onMounted(() => {
  update()
  // 测量节点内容(文案变化)或容器宽度变化时重测;测量节点隐藏且不参与布局,无循环风险
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(update)
    observer.observe(containerRef.value!)
    observer.observe(measureRef.value!)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <div ref="containerRef" class="basic-fit-lines">
    <!-- 隐藏测量节点:整句的单行自然宽度 -->
    <span ref="measureRef" class="basic-fit-lines__measure" aria-hidden="true">{{ singleLine }}</span>
    <div v-if="fitsSingleLine" class="basic-fit-lines__single">
      {{ singleLine }}
    </div>
    <template v-else>
      <BasicFitText v-for="(line, index) in lines" :key="index">
        {{ line }}
      </BasicFitText>
    </template>
  </div>
</template>

<style scoped>
.basic-fit-lines {
  position: relative;
  min-width: 0;
  max-width: 100%;
  /* 兜底:极端场景(如祖先 flex 链未放开收缩)下测量失真时,溢出裁剪而非压到相邻元素 */
  overflow: hidden;
}

.basic-fit-lines__measure {
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
}

/* 判定为放得下时强制单行,避免测量误差导致意外折行 */
.basic-fit-lines__single {
  white-space: nowrap;
}
</style>
